/* global axios */
import ApiClient from './ApiClient';
import store from '../store';
import webhookService from '../services/pipeline/webhookService';

const positiveIntegerPathSegment = (value, name) => {
  const normalizedValue = String(value ?? '').trim();
  if (!/^[1-9]\d*$/.test(normalizedValue)) {
    throw new TypeError(`${name} must be a positive integer`);
  }
  return encodeURIComponent(normalizedValue);
};

// Sub-recurso de um card no namespace canônico:
//   /api/v1/accounts/:id/pipeline/cards/:card_id/<resource>
// Serve anexos, notas, lead_scores etc. — o nome antigo
// (`attachmentCollectionPath`) sugeria que só valia para anexos e já
// induziu leitura errada em review.
const cardResourcePath = (accountId, cardId, resource) =>
  `/api/v1/accounts/${positiveIntegerPathSegment(
    accountId,
    'accountId'
  )}/pipeline/cards/${positiveIntegerPathSegment(
    cardId,
    'cardId'
  )}/${resource}`;

const pipelineCardsCollectionPath = accountId =>
  `/api/v1/accounts/${positiveIntegerPathSegment(
    accountId,
    'accountId'
  )}/pipeline_cards`;

const cardResourceMemberPath = (accountId, cardId, resource, attachmentId) =>
  `${cardResourcePath(
    accountId,
    cardId,
    resource
  )}/${positiveIntegerPathSegment(attachmentId, 'attachmentId')}`;

class PipelineCardsAPI extends ApiClient {
  constructor() {
    super('pipeline_cards', { accountScoped: true });
  }

  // Audit 2026-05-07 — bug Vai Pri / ClickHype: este getter retornava
  // `currentUser.account_id`, que vem do store de auth e dessincroniza da URL
  // durante transições de account (set_active_account é assíncrono). Resultado:
  // requests saíam para `/accounts/<conta-anterior>/cards/<cardId-stale>/...`
  // → 404 protetivos no console (backend nega corretamente, mas UX é ruim).
  //
  // Fix: usar a URL como única fonte de verdade — bate com a route ativa do
  // Vue Router e com tudo mais que `ApiClient.accountIdFromRoute` já usa.
  get accountId() {
    return this.accountIdFromRoute;
  }

  // NOOVI: import CSV de cards (Gap C — fase-10). Usa a rota namespaced
  // pipeline/cards/import (controller dedicado CardImports), multipart.
  importCards(pipelineId, file) {
    const formData = new FormData();
    formData.append('import_file', file);
    formData.append('pipeline_id', pipelineId);
    return axios.post(
      `/api/v1/accounts/${this.accountIdFromRoute}/pipeline/cards/import`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  // NOOVI: traduz o objeto de filtros do board (PipelineFilter.vue, formato
  // aninhado) para os query params planos que o backend #index aceita
  // (filtragem server-side — antes era 100% client-side e quebrava com a
  // paginação por stage). Só inclui chaves com valor real.
  // eslint-disable-next-line class-methods-use-this
  buildFilterParams(filters = {}) {
    const params = {};
    if (!filters) return params;
    if (Array.isArray(filters.labels) && filters.labels.length) {
      params['labels[]'] = filters.labels;
    }
    if (Array.isArray(filters.priority) && filters.priority.length) {
      params['priority[]'] = filters.priority;
    }
    if (Array.isArray(filters.stages) && filters.stages.length) {
      params['stages[]'] = filters.stages;
    }
    const value = filters.value || {};
    if (value.min !== null && value.min !== undefined && value.min !== '') {
      params.value_min = value.min;
    }
    if (value.max !== null && value.max !== undefined && value.max !== '') {
      params.value_max = value.max;
    }
    if (filters.agent_id !== null && filters.agent_id !== undefined) {
      params.agent_id = filters.agent_id;
    }
    const date = filters.date || {};
    if (date.start) params.date_start = date.start;
    if (date.end) params.date_end = date.end;
    if (filters.status) params.status = filters.status;
    if (filters.sla_exceeded) params.sla_exceeded = true;
    if (filters.search?.trim()) params.search = filters.search.trim();
    return params;
  }

  // NOOVI: baixa o CSV MODELO de import (Gap C). Blob para preservar o BOM
  // UTF-8 e acentos. O usuário preenche e reenvia via importCards.
  downloadImportTemplate() {
    return axios.get(
      `/api/v1/accounts/${this.accountIdFromRoute}/pipeline/cards/template`,
      { responseType: 'blob' }
    );
  }

  // NOOVI: exporta os cards como CSV (Gap C — export). Envia os MESMOS filtros
  // do board para que o export reflita exatamente o que está visível.
  exportCards(pipelineId, filters = {}) {
    const params = { ...this.buildFilterParams(filters) };
    if (pipelineId) params.pipeline_id = pipelineId;
    return axios.get(
      `/api/v1/accounts/${this.accountIdFromRoute}/pipeline/cards/export`,
      { params, responseType: 'blob' }
    );
  }

  // Audit 2026-04-25 P1.1 — passa pipeline_stage e cursor adicionalmente.
  // Backend faz cursor pagination por (position, id) quando `cursor` está
  // presente. Caso contrário, mantém offset pagination retrocompatível.
  // contact_id / exclude_id adicionados para tab RelatedItems.
  // filters (NOOVI): objeto de filtros do board aplicado server-side.
  async getCards(
    pipelineId,
    {
      limit = 50,
      offset = 0,
      pipeline_stage = null,
      cursor = null,
      contact_id = null,
      exclude_id = null,
      filters = null,
    } = {},
    { accountId = null, signal = null } = {}
  ) {
    const params = {
      limit,
      ...this.buildFilterParams(filters),
    };
    if (pipelineId) {
      params.pipeline_id = pipelineId;
    } else if (!contact_id) {
      // Only fall back to selected pipeline when not doing a contact-scoped query
      const selectedId = store.getters['pipelines/getSelectedPipeline']?.id;
      if (selectedId) params.pipeline_id = selectedId;
    }
    if (cursor) {
      params.cursor = cursor;
    } else {
      params.offset = offset;
    }
    if (pipeline_stage) {
      params.pipeline_stage = pipeline_stage;
    }
    if (contact_id) {
      params.contact_id = contact_id;
    }
    if (exclude_id) {
      params.exclude_id = exclude_id;
    }

    let requestUrl = this.url;
    if (accountId !== null && accountId !== undefined) {
      requestUrl = `/api/v1/accounts/${positiveIntegerPathSegment(
        accountId,
        'accountId'
      )}/pipeline_cards`;
    }

    // Use axios.get directly because ApiClient.get() doesn't accept params.
    // accountId/signal let long-lived views keep every cursor request bound to
    // the tenant snapshot that started the pagination sequence.
    return axios.get(requestUrl, { params, ...(signal ? { signal } : {}) });
  }

  async getCardsByStage(
    pipelineId,
    stage,
    { limit = 50, offset = 0, cursor = null } = {}
  ) {
    const params = {
      pipeline_id: pipelineId,
      pipeline_stage: stage,
      limit,
    };
    if (cursor) {
      params.cursor = cursor;
    } else {
      params.offset = offset;
    }

    return axios.get(this.url, { params });
  }

  async getCard(cardId, { accountId = null, signal = null } = {}) {
    const cardPathSegment = positiveIntegerPathSegment(cardId, 'cardId');
    const requestUrl =
      accountId === null || accountId === undefined
        ? `${this.url}/${cardPathSegment}`
        : `${pipelineCardsCollectionPath(accountId)}/${cardPathSegment}`;
    const response = signal
      ? await axios.get(requestUrl, { signal })
      : await axios.get(requestUrl);
    return response;
  }

  async createCard(data, { accountId = null } = {}) {
    const requestUrl =
      accountId === null || accountId === undefined
        ? this.url
        : pipelineCardsCollectionPath(accountId);
    const response = await axios.post(requestUrl, {
      pipeline_card: data,
    });
    await webhookService.notifyCardCreated(response.data);
    return response;
  }

  async updateCard(cardId, data, options = {}) {
    // Use currentCard from options if provided, avoiding extra API call
    const currentCard = options.currentCard || null;
    const cardPathSegment = positiveIntegerPathSegment(cardId, 'cardId');
    const requestUrl =
      options.accountId === null || options.accountId === undefined
        ? `${this.url}/${cardPathSegment}`
        : `/api/v1/accounts/${positiveIntegerPathSegment(
            options.accountId,
            'accountId'
          )}/pipeline_cards/${cardPathSegment}`;

    const payload = { pipeline_card: data };
    const response = options.signal
      ? await axios.put(requestUrl, payload, { signal: options.signal })
      : await axios.put(requestUrl, payload);

    // Only compute changes if we have currentCard and webhook is needed
    if (currentCard) {
      const changes = {};
      Object.keys(data).forEach(key => {
        if (JSON.stringify(currentCard[key]) !== JSON.stringify(data[key])) {
          changes[key] = {
            from: currentCard[key],
            to: data[key],
          };
        }
      });

      if (Object.keys(changes).length > 0) {
        await webhookService.notifyCardUpdated(response.data, changes);
      }
    } else {
      // No currentCard provided, send update without detailed changes
      await webhookService.notifyCardUpdated(response.data, data);
    }

    return response;
  }

  async deleteCard(cardId, options = {}) {
    // Use currentCard from options if provided, avoiding extra API call
    const cardData = options.currentCard || { id: cardId };
    const cardPathSegment = positiveIntegerPathSegment(cardId, 'cardId');
    const requestUrl =
      options.accountId === null || options.accountId === undefined
        ? `${this.url}/${cardPathSegment}`
        : `${pipelineCardsCollectionPath(
            options.accountId
          )}/${cardPathSegment}`;

    const response = options.signal
      ? await axios.delete(requestUrl, { signal: options.signal })
      : await axios.delete(requestUrl);

    await webhookService.notifyCardDeleted(cardData);
    return response;
  }

  // Fase 4.1 — Bulk delete via single endpoint (replaces N parallel
  // deleteItem requests). Returns { deleted, failed, failed_items }.
  // pipelineStage is optional — when present, only cards in that stage
  // (intersected with cardIds) are discarded.
  async bulkDelete({ cardIds, pipelineStage = null, reason = null } = {}) {
    const payload = { card_ids: cardIds };
    if (pipelineStage) payload.pipeline_stage = pipelineStage;
    if (reason) payload.reason = reason;
    return axios.post(
      `/api/v1/accounts/${this.accountId}/pipeline/bulk_actions/delete`,
      payload
    );
  }

  // Fase 4.2 — Bulk set priority via single endpoint.
  async bulkSetPriority({ cardIds, priority, pipelineStage = null } = {}) {
    const payload = { card_ids: cardIds, priority };
    if (pipelineStage) payload.pipeline_stage = pipelineStage;
    return axios.post(
      `/api/v1/accounts/${this.accountId}/pipeline/bulk_actions/set_priority`,
      payload
    );
  }

  async moveToStage(
    cardId,
    pipelineStage,
    fromStage = null,
    { lostReason = null, wonValue = null, wonNote = null } = {}
  ) {
    // fromStage can be passed from caller to avoid extra API call
    const params = {
      pipeline_stage: pipelineStage,
      stage_entered_at: new Date().toISOString(),
    };

    // Incluir motivo de perda se fornecido
    if (lostReason) {
      params.lost_reason = lostReason;
    }

    // Incluir valor e nota de ganho se fornecidos
    if (wonValue !== null) {
      params.won_value = wonValue;
    }
    if (wonNote) {
      params.won_note = wonNote;
    }

    const response = await axios.post(
      `${this.url}/${cardId}/move_to_stage`,
      params
    );

    // The response from move_to_stage returns the updated card
    const updatedCard = response.data || {
      id: cardId,
      pipeline_stage: pipelineStage,
    };

    await webhookService.notifyStageChange(
      updatedCard,
      fromStage,
      pipelineStage
    );

    return updatedCard;
  }

  async reorderCards(positions, existingCards = []) {
    const pipelineId =
      positions[0]?.pipeline_id || existingCards[0]?.pipeline_id;
    const payload = pipelineId
      ? { pipeline_id: pipelineId, positions }
      : { positions };
    const response = await axios.post(`${this.url}/reorder`, payload);

    // Build changes from existingCards if provided, otherwise use positions data
    const changes = positions.map(pos => {
      const existingCard = existingCards.find(card => card.id === pos.id);
      return {
        card_id: pos.id,
        old_position: existingCard?.position,
        new_position: pos.position,
        old_stage: existingCard?.pipeline_stage,
        new_stage: pos.pipeline_stage,
      };
    });

    await webhookService.notifyCardReordered(response.data, changes);
    return response;
  }

  async startTimer(cardId) {
    const response = await this.updateCard(cardId, {
      timer_started_at: new Date().toISOString(),
    });

    await webhookService.notifyTimerStarted(response.data);
    return response;
  }

  async stopTimer(cardId) {
    const response = await this.updateCard(cardId, {
      timer_started_at: null,
    });

    const duration = response.data.timer_duration;
    await webhookService.notifyTimerStopped(response.data, duration);
    return response;
  }

  async getCardByConversationId(
    conversationDisplayId,
    { accountId = null } = {}
  ) {
    // Validate that we have a valid conversation display ID
    // This prevents the API from returning ALL cards when no filter is applied
    if (!conversationDisplayId || conversationDisplayId <= 0) {
      return { data: [] };
    }

    // Use axios.get directly because ApiClient.get() doesn't accept params
    const requestUrl =
      accountId === null || accountId === undefined
        ? this.url
        : pipelineCardsCollectionPath(accountId);
    const response = await axios.get(requestUrl, {
      params: {
        conversation_display_id: conversationDisplayId,
        // Opt into BOTH link types (primary + multi-conversation join).
        // Without this the conversation's Pipeline panel showed "no card
        // linked" for a conversation attached through the card's Conversations
        // tab. The API default stays primary-only for external consumers.
        include_linked_conversations: true,
      },
    });

    // Handle paginated response format (data.data) vs legacy (data as array)
    if (response.data?.data && Array.isArray(response.data.data)) {
      return { data: response.data.data };
    }
    return response;
  }

  // Note: Automation methods moved to pipelineAutomations.js
  // Use: import PipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';

  // NOOVI: Lixeira por pipeline (Lixeira/retention 30 dias). Usa as rotas
  // NAMESPACED /pipeline/cards (não a legada /pipeline_cards). Admin-only no
  // backend. `discarded` lista os cards descartados do pipeline; `restore`
  // restaura; `permanently_delete` exclui definitivamente (só cards já
  // descartados).
  getDiscardedCards({ pipelineId, page = 1, perPage = 25 } = {}) {
    return axios.get(
      `/api/v1/accounts/${this.accountId}/pipeline/cards/discarded`,
      { params: { pipeline_id: pipelineId, page, per_page: perPage } }
    );
  }

  restoreCard(cardId) {
    return axios.post(
      `/api/v1/accounts/${this.accountId}/pipeline/cards/${cardId}/restore`
    );
  }

  permanentlyDeleteCard(cardId) {
    return axios.delete(
      `/api/v1/accounts/${this.accountId}/pipeline/cards/${cardId}/permanently_delete`
    );
  }

  // Owner Management Methods
  // eslint-disable-next-line class-methods-use-this
  async assignOwner(accountId, cardId, data) {
    const response = await axios.patch(
      `/api/v1/accounts/${accountId}/pipeline/cards/${cardId}/assign`,
      data
    );
    return response;
  }

  // eslint-disable-next-line class-methods-use-this
  async bulkAssignOwner(accountId, data) {
    const response = await axios.post(
      `/api/v1/accounts/${accountId}/pipeline/cards/bulk_assign`,
      data
    );
    return response;
  }

  // Pipeline Analytics Methods
  // eslint-disable-next-line class-methods-use-this
  async getUserPipeline(accountId, userId) {
    const response = await axios.get(
      `/api/v1/accounts/${accountId}/pipeline/analytics/pipeline/${userId}`
    );
    return response;
  }

  // eslint-disable-next-line class-methods-use-this
  async getTeamPipeline(accountId) {
    const response = await axios.get(
      `/api/v1/accounts/${accountId}/pipeline/analytics/team_pipeline`
    );
    return response;
  }

  // Lead Score Methods
  async recalculateLeadScore(cardId) {
    const response = await axios.post(
      `${cardResourcePath(this.accountId, cardId, 'lead_scores/recalculate')}`
    );
    return response;
  }

  async overrideLeadScore(cardId, score, reason) {
    const response = await axios.post(
      `${cardResourcePath(this.accountId, cardId, 'lead_scores/override')}`,
      { score, reason }
    );
    return response;
  }

  // Deal Status Methods
  // Qualification Checklist Methods
  async updateQualificationChecklist(cardId, checklist) {
    const response = await axios.patch(
      `${this.url}/${cardId}/update_qualification_checklist`,
      { qualification_checklist: checklist }
    );
    return response;
  }

  async recalculateScore(cardId) {
    const response = await axios.post(
      `${this.url}/${cardId}/recalculate_score`
    );
    return response;
  }

  // Sequence Methods
  getCardSequences(cardId) {
    return axios.get(
      `/api/v1/accounts/${this.accountId}/pipeline/cards/${cardId}/sequences`
    );
  }

  startSequence(cardId, definitionId) {
    return axios.post(
      `/api/v1/accounts/${this.accountId}/pipeline/cards/${cardId}/sequences`,
      { definition_id: definitionId }
    );
  }

  pauseSequence(cardId, sequenceId) {
    return axios.patch(
      `/api/v1/accounts/${this.accountId}/pipeline/cards/${cardId}/sequences/${sequenceId}/pause`
    );
  }

  resumeSequence(cardId, sequenceId) {
    return axios.patch(
      `/api/v1/accounts/${this.accountId}/pipeline/cards/${cardId}/sequences/${sequenceId}/resume`
    );
  }

  cancelSequence(cardId, sequenceId) {
    return axios.delete(
      `/api/v1/accounts/${this.accountId}/pipeline/cards/${cardId}/sequences/${sequenceId}`
    );
  }

  // Phase 1.2 — Mark current step as completed and advance to the next.
  // Backend endpoint: PATCH /pipeline/cards/:card_id/sequences/:id/complete_step
  // (implemented by Phase 1.1 backend agent — may return 404 during parallel dev)
  completeSequenceStep(cardId, sequenceId) {
    return axios.patch(
      `/api/v1/accounts/${this.accountId}/pipeline/cards/${cardId}/sequences/${sequenceId}/complete_step`
    );
  }
}

export default new PipelineCardsAPI();

// Attachment Methods - Added at end of class
PipelineCardsAPI.prototype.getAttachments = async function getAttachments(
  cardId,
  { accountId = this.accountId, signal = null } = {}
) {
  const url = cardResourcePath(accountId, cardId, 'attachments');
  const response = signal
    ? await axios.get(url, { signal })
    : await axios.get(url);
  return response;
};

PipelineCardsAPI.prototype.uploadAttachment = async function uploadAttachment(
  cardId,
  file,
  config = {}
) {
  const { accountId = this.accountId, ...requestConfig } = config;
  const formData = new FormData();
  formData.append('attachment', file);

  const response = await axios.post(
    cardResourcePath(accountId, cardId, 'attachments'),
    formData,
    {
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response;
};

PipelineCardsAPI.prototype.downloadAttachment =
  async function downloadAttachment(
    cardId,
    attachmentId,
    { accountId = this.accountId, signal = null } = {}
  ) {
    const response = await axios.get(
      cardResourceMemberPath(accountId, cardId, 'attachments', attachmentId),
      {
        responseType: 'blob',
        ...(signal ? { signal } : {}),
      }
    );
    return response;
  };

PipelineCardsAPI.prototype.deleteAttachment = async function deleteAttachment(
  cardId,
  attachmentId,
  { accountId = this.accountId, signal = null } = {}
) {
  const url = cardResourceMemberPath(
    accountId,
    cardId,
    'attachments',
    attachmentId
  );
  const response = signal
    ? await axios.delete(url, { signal })
    : await axios.delete(url);
  return response;
};

PipelineCardsAPI.prototype.uploadNoteAttachment =
  async function uploadNoteAttachment(cardId, file, config = {}) {
    const { accountId = this.accountId, ...requestConfig } = config;
    const formData = new FormData();
    formData.append('attachment', file);

    return axios.post(
      cardResourcePath(accountId, cardId, 'note_attachments'),
      formData,
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  };

PipelineCardsAPI.prototype.deleteNoteAttachment =
  async function deleteNoteAttachment(
    cardId,
    attachmentId,
    { accountId = this.accountId, signal = null } = {}
  ) {
    const url = cardResourceMemberPath(
      accountId,
      cardId,
      'note_attachments',
      attachmentId
    );
    const response = signal
      ? await axios.delete(url, { signal })
      : await axios.delete(url);
    return response;
  };
